import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { organizationQueryOptions } from '@/api/queries/useOrganization';
import { useInitializeUnresolvedDepartments } from '@/api/queries/useInitializeUnresolvedDepartments';
import { useDepartments } from '@/api/queries/useDepartments';
import type { Organization } from '@bliks-io/contracts';
import type { WizardProgress } from './wizardSteps';

export function toWizardProgress(
  organization: Organization | undefined,
  unresolvedDepartments: number,
  resolvedDepartments: number
): WizardProgress {
  const initialization = organization?.initialization;
  return {
    departmentsDone: !!initialization?.departments,
    usersDone: !!initialization?.users,
    orgFunctionsDone: !!initialization?.orgFunctions,
    teamsDone: !!initialization?.teams,
    systemsDone: !!initialization?.systems,
    processesDone: !!initialization?.processes,
    completedAt: initialization?.completedAt,
    unresolvedDepartments,
    resolvedDepartments
  };
}

/**
 * Server-derived wizard progress — the only persistence layer for
 * pause/resume. Local Excel drafts intentionally stay client-side; the
 * welcome copy promises resume of saved steps only.
 */
export function useWizardProgress() {
  const { data: organization, isLoading: isLoadingOrganization } =
    useQuery(organizationQueryOptions);
  const { data: unresolvedDepartments, isLoading: isLoadingUnresolvedDepartments } =
    useInitializeUnresolvedDepartments();
  const { data: departments, isLoading: isLoadingDepartments } = useDepartments();

  // Signal for the Manager-auflösen rail entry staying visible once any
  // resolution happened, even after the open list is empty (see wizardSteps).
  const resolvedDepartments = useMemo(
    () => (departments ?? []).filter((department) => !!department.manager).length,
    [departments]
  );

  return {
    progress: toWizardProgress(
      organization,
      unresolvedDepartments?.length ?? 0,
      resolvedDepartments
    ),
    isLoading: isLoadingOrganization || isLoadingUnresolvedDepartments || isLoadingDepartments
  };
}
